import type {
  EnrollmentDisplayStatus,
  EnrollmentTransactionType,
} from '../../services/clinicsApi';

/**
 * How each enrollment state is described to an agency that has never seen Stedi.
 *
 * The clearinghouse is never named. An agency has no account there and cannot
 * act on the distinction between "the clearinghouse is working on it" and "the
 * payer is" — both mean submitted, in flight, nothing for you to do. So both
 * are "In progress".
 *
 * Not Stedi's own PROVIDER_ACTION_REQUIRED either: that status stays set after
 * the provider's last task is done, and reading it literally would send someone
 * looking for work that no longer exists.
 */
export const ENROLLMENT_STATUS_LABELS: Record<EnrollmentDisplayStatus, string> =
  {
    // The same words as the payer row's summary, so the two read as one.
    action_required: 'Needs action',
    waiting: 'Submitted',
    rejected: 'Rejected',
    live: 'Live',
    not_started: 'Not started',
    not_required: 'Not required',
    not_available: 'Not available',
    unknown: 'Unknown',
  };

export type EnrollmentBadgeVariant = 'destructive' | 'secondary' | 'outline';

export const ENROLLMENT_STATUS_VARIANTS: Record<
  EnrollmentDisplayStatus,
  EnrollmentBadgeVariant
> = {
  action_required: 'destructive',
  rejected: 'destructive',
  waiting: 'secondary',
  live: 'secondary',
  not_started: 'outline',
  not_required: 'outline',
  not_available: 'outline',
  unknown: 'outline',
};

/**
 * Live is the only state that means finished, and it needs to look like it.
 *
 * Every other resting state is `secondary` too, so on the variants alone Live
 * read as one more neutral holding state. Green follows the convention already
 * in the app — see GoalSyncSection's "Completed" badge — rather than adding a
 * `success` variant to the shared design system, which AGENTS.md says to
 * discuss first.
 */
export const ENROLLMENT_STATUS_CLASSES: Partial<
  Record<EnrollmentDisplayStatus, string>
> = {
  live: 'border-transparent bg-green-100 text-green-800',
};

/**
 * The statuses the tab opens with, pre-selected in the Status filter.
 *
 * The ones left out — not required, not available, unknown — are the majority
 * of rows and none of them needs anything done. In progress is left out for the
 * same reason and not a different one: it means the clearinghouse or the payer
 * is working on it, so listing it by default buries the rows somebody here has
 * to act on.
 *
 * All of them stay a visible, unchecked filter rather than a hidden rule, so
 * the count above the table is explainable and one click brings them back. That
 * checkbox is the toggle; there is no second mechanism for it.
 */
export const DEFAULT_STATUSES: EnrollmentDisplayStatus[] = [
  'action_required',
  'rejected',
  'not_started',
  'live',
];

export const TRANSACTION_LABELS: Record<EnrollmentTransactionType, string> = {
  institutionalClaimSubmission: 'Claims',
  eligibilityCheck: 'Eligibility',
  claimPayment: 'Remittance (ERA)',
};

const ENROLLMENT_TYPE_LABELS: Record<string, string> = {
  ONE_CLICK: 'One-click',
  MULTI_STEP: 'Multi-step',
};

const ENROLLMENT_TIMEFRAME_LABELS: Record<string, string> = {
  INSTANT: 'Instant',
  HOURS: 'Hours',
  DAYS: 'Days',
  WEEKS: 'Weeks',
  OVER_4_WEEKS: 'Over 4 weeks',
};

export const enrollmentTypeLabel = (value?: string | null): string =>
  (value && ENROLLMENT_TYPE_LABELS[value]) || '—';

export const enrollmentTimeframeLabel = (value?: string | null): string =>
  (value && ENROLLMENT_TIMEFRAME_LABELS[value]) || '—';

/** Order the team chases them in: claims, then eligibility, then remittance. */
export const TRANSACTION_ORDER: EnrollmentTransactionType[] = [
  'institutionalClaimSubmission',
  'eligibilityCheck',
  'claimPayment',
];

/**
 * The statuses that mean a person still has to do something.
 *
 * Must match `COUNTED_AS_OUTSTANDING` in the backend's clinic_enrollment_view —
 * the tab count endpoint uses that one, and this derives the same number from
 * rows already loaded so the badge moves with a Refresh. Change one and change
 * the other, or the badge says one thing before the tab is opened and another
 * after.
 */
export const OUTSTANDING_STATUSES: EnrollmentDisplayStatus[] = [
  'action_required',
  'rejected',
];
